import type { ComponentPropsWithoutRef } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'
import styles from './TextField.module.css'

type NativeProps = Omit<ComponentPropsWithoutRef<'input'>, 'id' | 'className'>

interface TextFieldProps extends NativeProps {
  label: string
  name: string
  /** Renders a `textarea` instead of an `input`. */
  multiline?: boolean
  /** Initial visible rows when `multiline`. */
  rows?: number
  /** Validation message; also marks the control invalid. */
  error?: string | undefined
  className?: string
}

/**
 * Labelled text input.
 *
 * Wires the label, the control and any error message together with generated
 * ids so every field is announced correctly without callers having to think
 * about `htmlFor` or `aria-describedby`.
 */
export const TextField = ({
  label,
  name,
  multiline = false,
  rows = 4,
  error,
  className,
  ...rest
}: TextFieldProps) => {
  const id = useId()
  const errorId = `${id}-error`

  const controlProps = {
    id,
    name,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    className: cn(styles.control, multiline && styles.textarea),
  } as const

  return (
    <div className={cn(styles.field, className)}>
      <label htmlFor={id} className={`t-12 ${styles.label}`}>
        {label}
      </label>

      {multiline ? (
        <textarea
          {...(rest as ComponentPropsWithoutRef<'textarea'>)}
          {...controlProps}
          rows={rows}
        />
      ) : (
        <input {...rest} {...controlProps} />
      )}

      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
